import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Bell, Send, Users, CheckSquare, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AppNotifications = () => {
  const { language } = useLanguage();
  const ro = language === 'ro';

  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/notifications/users`)
      .then(res => setUsers(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoadingUsers(false));
  }, []);

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.company_name?.toLowerCase().includes(q)
    );
  });

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.add(u.id));
        return next;
      });
    }
  };

  const toggleUser = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim() || selectedIds.size === 0) return;
    setSending(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/admin/notifications/send`, {
        title: title.trim(),
        body: body.trim(),
        user_ids: Array.from(selectedIds),
      });
      setResults(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          {ro ? 'Notificări Aplicație' : 'App Notifications'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {ro ? 'Trimite notificări push utilizatorilor aplicației' : 'Send push notifications to app users'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compose */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Bell className="w-4 h-4 text-primary" />
              {ro ? 'Notificare' : 'Notification'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 flex-1">
            <Input
              placeholder={ro ? 'Titlu notificare...' : 'Notification title...'}
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
            <textarea
              className="w-full flex-1 min-h-[160px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={ro ? 'Mesajul notificării...' : 'Notification message...'}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {users.length} {ro ? 'utilizatori cu notificări active' : 'users with notifications enabled'}
              </span>
              <Button
                onClick={handleSend}
                disabled={sending || !title.trim() || !body.trim() || selectedIds.size === 0}
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {sending
                  ? (ro ? 'Se trimite...' : 'Sending...')
                  : `${ro ? 'Trimite' : 'Send'} (${selectedIds.size})`}
              </Button>
            </div>

            {results && (
              <div className={`text-sm px-3 py-2 rounded ${results.sent > 0 ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                {ro ? `Trimis către ${results.sent} utilizatori` : `Sent to ${results.sent} users`}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Users className="w-4 h-4 text-primary" />
              {ro ? 'Destinatari' : 'Recipients'}
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {selectedIds.size} {ro ? 'selectați' : 'selected'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={ro ? 'Caută utilizator...' : 'Search user...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {loadingUsers ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {ro
                  ? 'Niciun utilizator nu are notificările activate încă. Vor apărea după ce instalează și deschid aplicația.'
                  : 'No users have notifications enabled yet. They will appear after installing and opening the app.'}
              </div>
            ) : (
              <>
                <div
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none border-b pb-2"
                  onClick={toggleAll}
                >
                  {allSelected
                    ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                    : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <span className="text-sm font-medium">{ro ? 'Selectează toți' : 'Select all'}</span>
                </div>
                <div className="space-y-1 max-h-[280px] overflow-y-auto">
                  {filteredUsers.map(u => (
                    <div
                      key={u.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none"
                      onClick={() => toggleUser(u.id)}
                    >
                      {selectedIds.has(u.id)
                        ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                        : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {u.email}{u.company_name ? ` · ${u.company_name}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {ro ? 'Niciun utilizator găsit' : 'No users found'}
                    </p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppNotifications;
